import i18n from 'i18next';
import { IActiveCampaign, IDesignItem } from 'src/common/interfaces/campaigns.interfaces';
import { HierarchyType } from 'src/enums/compare-estimates';
import { pick } from 'lodash';
import { SimulationChartType } from 'src/enums/campaigns';
import { getDurationOrCost, getMetersPerDay } from 'src/store/entities/simulations/simulations';
import translations from 'src/internationalisation/translation-map.json';
import { probabilities } from 'src/enums/simulations';
import { displayNumber, round, unum } from '@oliasoft-open-source/units';
import { HOURS_PER_DAY } from 'src/enums/general';
import { shownCumulativeProbabilities } from 'src/views/simulations/simulations';

type THeaderLabel = {
  label: string;
  identifier: string;
};

const getLearningCurveAdjustment = (adjustment: number | boolean) =>
  typeof adjustment === 'number' ? adjustment / 100 : 0;

const getDayRateCost = (simulation: any, probability: string | number) => {
  const probabilityIndex =
    `${probability}` === probabilities.MEAN
      ? -1
      : simulation?.percentiles?.findIndex((percentile: number) => percentile === +probability);

  return (
    simulation?.operations?.reduce((sum: number, operation: any) => {
      const duration = probabilityIndex === -1 ? operation.average : operation.percentiles?.[probabilityIndex];

      return sum + (duration ?? 0) * (operation.variable_cost ?? 0);
    }, 0) ?? 0
  );
};

export const getLearningCurveAdjustedValue = (
  simulation: any,
  value: number,
  adjustment: number | boolean,
  dataEntity: number,
  probability: string | number,
) => {
  const learningCurveAdjustment = getLearningCurveAdjustment(adjustment);

  if (!learningCurveAdjustment) {
    return value;
  }

  if (dataEntity === 1) {
    return value - getDayRateCost(simulation, probability) * learningCurveAdjustment;
  }

  return round(value * (1 - learningCurveAdjustment), 3);
};

export const getHeaderLabels = (t: (key: string) => string, translations: any, isConcept = false) => [
  ...(isConcept
    ? [
        {
          label: '',
          identifier: HierarchyType.UseExisting,
        },
        {
          label: t(translations.compare_automationMethod),
          identifier: HierarchyType.AutomationMethod,
        },
      ]
    : []),
  { label: t(translations.estimate), identifier: HierarchyType.EstimateName },
  { label: t(translations.design), identifier: HierarchyType.DesignName },
  { label: t(translations.wellbore), identifier: HierarchyType.WellboreName },
  { label: t(translations.well), identifier: HierarchyType.WellName },
  { label: t(translations.site), identifier: HierarchyType.SiteName },
  { label: t(translations.field), identifier: HierarchyType.FieldName },
  { label: t(translations.country), identifier: HierarchyType.CountryName },
];

export const getHeaderRow = (headerLabels: THeaderLabel[]) =>
  headerLabels.map(({ label, identifier }: { label: string; identifier: string }) => {
    let width;

    if (identifier === HierarchyType.AutomationMethod) {
      width = '20%';
    } else if (identifier === HierarchyType.UseExisting) {
      width = '4%';
    } else {
      width = 'auto';
    }
    return {
      value: label,
      width,
    };
  });

export const getHeaderFilter = (
  headerLabels: THeaderLabel[],
  filters: { [key: string]: string },
  setFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
  t: (key: string) => string,
  translations: any,
  isTableModal = false,
  setSelectedPage?: React.Dispatch<React.SetStateAction<number>>,
) =>
  headerLabels.map((header: THeaderLabel) => {
    const { identifier } = header;
    const filterValue = filters[identifier] ?? '';
    return header.identifier === HierarchyType.AutomationMethod || header.identifier === HierarchyType.UseExisting
      ? {}
      : {
          key: identifier,
          value: filterValue,
          type: 'Input',
          placeholder: t(translations.search),
          onChange: (ev: React.ChangeEvent<HTMLSelectElement>) => {
            if (isTableModal && setSelectedPage) {
              setSelectedPage(1);
            }
            setFilters((prev) => ({
              ...prev,
              [identifier]: ev.target.value,
            }));
          },
        };
  });

export const getFilteredData = (designs: IDesignItem[], filters: { [key: string]: string }) =>
  designs.filter((design) =>
    Object.keys(filters).every((key) => {
      const filterValue = filters[key];
      const designValue = design[key as keyof IDesignItem];

      return (
        filterValue === '' ||
        (designValue != null && designValue.toString().toLowerCase().includes(filterValue.toLowerCase()))
      );
    }),
  );

export const getRows = (
  filteredData: IDesignItem[],
  designsValue: IDesignItem[],
  setDesignsValue: React.Dispatch<React.SetStateAction<IDesignItem[]>>,
) =>
  filteredData.map((row) => {
    const formattedData = {
      ...row,
    };
    const checked = designsValue.some(
      (item) =>
        (row.datasetId && item.datasetId === row.datasetId) || (row.projectId && item.projectId === row.projectId),
    );
    return {
      cells: [
        {
          type: 'CheckBox',
          checked,
          onChange: () => {
            if (checked) {
              setDesignsValue((prev) =>
                prev.filter((item) =>
                  row.datasetId ? item.datasetId !== row.datasetId : item.projectId !== row.projectId,
                ),
              );
            } else {
              setDesignsValue((prev) => [...prev, row]);
            }
          },
        },
        ...Object.entries(
          pick(formattedData, [
            HierarchyType.EstimateName,
            HierarchyType.DesignName,
            HierarchyType.WellboreName,
            HierarchyType.WellName,
            HierarchyType.SiteName,
            HierarchyType.FieldName,
            HierarchyType.CountryName,
          ]),
        ).map(([key, value]) => ({
          key,
          value,
        })),
      ],
    };
  });

export const generateTableRows = (
  chartType: string,
  dataEntity: number,
  campaign: IActiveCampaign,
  t: (key: string) => string,
  translations: any,
  depthUnit: string,
) => {
  let estimateRows = [];
  let additionalRows = [];
  let campaignTotal;
  const cumulativeDurations: number[] = Array(Object.keys(probabilities).length).fill(0);
  const adjustedCumulativeDurations: number[] = Array(Object.keys(probabilities).length).fill(0);
  const learningCurveReductionDurations: number[] = Array(Object.keys(probabilities).length).fill(0);
  const showAdjustedColumns =
    (chartType === SimulationChartType.METERS_PER_DAY || chartType === SimulationChartType.TOTAL_DAYS) &&
    campaign.learningCurve;
  const getAdjustedValue = (
    simulation: any,
    value: number,
    adjustment: number | boolean,
    percentile: string | number,
  ) =>
    campaign.learningCurve
      ? getLearningCurveAdjustedValue(simulation, value, adjustment, dataEntity, percentile)
      : value;

  if ('cumulative_subactivity_percentiles' in campaign?.campaignTotal?.simulation) {
    campaignTotal = campaign?.campaignTotal?.simulation;
  } else {
    return;
  }

  const boldStyle = { fontWeight: 'bold' };

  const subactivity = campaignTotal?.cumulative_subactivity_percentiles
    ?.filter((_: any, index: number) => shownCumulativeProbabilities.includes(index))
    ?.map((el: number) => el / 24);
  switch (chartType) {
    case SimulationChartType.METERS_PER_DAY:
    case SimulationChartType.TOTAL_DAYS:
      estimateRows = campaign.estimates.map((estimate) => {
        const values = Object.values(probabilities).flatMap<{
          value: string | number;
        }>((percentile, index) => {
          const duration = getDurationOrCost(estimate.simulation, percentile, dataEntity) ?? 0;
          const adjustedDuration = getAdjustedValue(estimate.simulation, duration, estimate.adjustment, percentile);

          if (chartType === SimulationChartType.METERS_PER_DAY) {
            const maxDepthPerDay = getMetersPerDay(estimate.simulation, duration, percentile);
            const adjustedMaxDepthPerDay = getMetersPerDay(estimate.simulation, adjustedDuration, percentile);
            const value = {
              value: round(unum(maxDepthPerDay as number, depthUnit, 'm'), 2),
            };
            const adjustedValue = {
              value: round(unum(adjustedMaxDepthPerDay as number, depthUnit, 'm'), 2),
            };

            return showAdjustedColumns ? [value, adjustedValue] : [value];
          }

          cumulativeDurations[index] += duration;
          adjustedCumulativeDurations[index] += adjustedDuration;
          learningCurveReductionDurations[index] += duration - adjustedDuration;

          const value = {
            value:
              dataEntity === 0
                ? displayNumber(round(duration, 1), {
                    scientific: false,
                  })
                : displayNumber(round(duration, 0), {
                    scientific: false,
                  }),
          };
          const adjustedValue = {
            value:
              dataEntity === 0
                ? displayNumber(round(adjustedDuration, 1), {
                    scientific: false,
                  })
                : displayNumber(round(adjustedDuration, 0), {
                    scientific: false,
                  }),
          };

          return showAdjustedColumns ? [value, adjustedValue] : [value];
        });

        return {
          cells: [{ value: estimate.estimateName }, ...values],
        };
      });

      additionalRows = [
        {
          active: true,
          cells: [
            {
              value: t(translations.campaigns_cumulativeTotal),
              style: boldStyle,
            },
            ...cumulativeDurations.flatMap((duration, index) => {
              const adjustedDuration = adjustedCumulativeDurations[index];
              const value = {
                value:
                  dataEntity === 0
                    ? displayNumber(round(duration, 1), { scientific: false })
                    : displayNumber(round(duration, 0), { scientific: false }),
                style: boldStyle,
              };
              const adjustedValue = {
                value:
                  dataEntity === 0
                    ? displayNumber(round(adjustedDuration, 1), {
                        scientific: false,
                      })
                    : displayNumber(round(adjustedDuration, 0), {
                        scientific: false,
                      }),
                style: boldStyle,
              };

              return showAdjustedColumns ? [value, adjustedValue] : [value];
            }),
          ],
        },
        ...(campaign?.campaignTotal?.simulation
          ? [
              {
                active: true,
                cells: [
                  {
                    value: t(translations.campaigns_campaignTotal),
                    style: boldStyle,
                  },
                  ...Object.values(probabilities).flatMap((percentile, index) => {
                    const duration = getDurationOrCost(campaign.campaignTotal.simulation, percentile, dataEntity) ?? 0;
                    const adjustedDuration = campaign.learningCurve
                      ? duration - learningCurveReductionDurations[index]
                      : duration;
                    const value = {
                      style: boldStyle,
                      value:
                        dataEntity === 0
                          ? displayNumber(round(duration, 1), {
                              scientific: false,
                            })
                          : displayNumber(round(duration, 0), {
                              scientific: false,
                            }),
                    };
                    const adjustedValue = {
                      style: boldStyle,
                      value:
                        dataEntity === 0
                          ? displayNumber(round(adjustedDuration, 1), {
                              scientific: false,
                            })
                          : displayNumber(round(adjustedDuration, 0), {
                              scientific: false,
                            }),
                    };

                    return showAdjustedColumns ? [value, adjustedValue] : [value];
                  }),
                ],
              },
            ]
          : []),
      ];
      return [...estimateRows, ...(chartType === SimulationChartType.TOTAL_DAYS ? additionalRows : [])];
    case SimulationChartType.CUMULATIVE_PROBABILITY:
      {
        const values =
          dataEntity === 0
            ? campaignTotal?.cumulative_probability?.time?.map((t) => t / HOURS_PER_DAY)
            : (campaignTotal?.cumulative_probability_cost?.costs ?? campaignTotal?.cumulative_probability_cost?.cost);
        const tableData = values?.filter((_, index) => shownCumulativeProbabilities.includes(index));
        const adjustedValues =
          campaign.learningCurve && values
            ? values.map((value, index) => {
                const percentile = index + 1;
                const learningCurveReduction = campaign.estimates.reduce((sum, estimate) => {
                  const estimateValue = getDurationOrCost(estimate.simulation, `${percentile}`, dataEntity) ?? 0;
                  const adjustedEstimateValue = getAdjustedValue(
                    estimate.simulation,
                    estimateValue,
                    estimate.adjustment,
                    `${percentile}`,
                  );

                  return sum + (estimateValue - adjustedEstimateValue);
                }, 0);

                return value - learningCurveReduction;
              })
            : [];
        const adjustedTableData = adjustedValues?.filter((_, index) => shownCumulativeProbabilities.includes(index));
        const estimatedLabel = t(
          dataEntity === 0 ? translations.simulations_estimatedTime : translations.simulations_estimatedCost,
        );
        const formatCumulativeProbabilityValue = (value: number) => {
          if (dataEntity === 0) {
            return { value: round(value, 2) };
          }

          return {
            value: displayNumber(round(value, 0), {
              scientific: false,
            }),
          };
        };

        estimateRows = [
          {
            cells: [
              {
                value: estimatedLabel,
              },
              ...(tableData?.map(formatCumulativeProbabilityValue) || []),
            ],
          },
          campaign.learningCurve &&
            adjustedTableData?.length > 0 && {
              cells: [
                {
                  value: `${t(translations.adjusted)} ${estimatedLabel}`,
                },
                ...adjustedTableData.map(formatCumulativeProbabilityValue),
              ],
            },
          dataEntity === 0 &&
            subactivity && {
              cells: [
                { value: t(translations.simulations_riskOnly) },
                ...subactivity?.map((el) => ({ value: round(el, 2) })),
              ],
            },
        ].filter(Boolean);
      }
      return estimateRows;
    default:
      return [];
  }
};

export const getLoaderText = (message: string | undefined, isGeneratingEstimates: boolean) => {
  if (message) {
    return message;
  }
  if (isGeneratingEstimates) {
    return i18n.t(translations.campaigns_generatingEstimates);
  }
  return i18n.t(translations.campaigns_runningCampaignSimulation);
};

export const colorFromUuid = (uuid: string, lightness: number = 60): string => {
  const saturation = 40;

  const hash = parseInt(uuid.replace(/-/g, '').slice(0, 8), 16);

  const hue = hash % 360;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const isCampaignSynced = (campaign: any) => {
  const { campaignTotal, estimates } = campaign || {};

  if (!campaignTotal) {
    return true;
  }
  const thresholdPercent = 1; // if deviation < 1% — synced

  const campaignTotalDuration = getDurationOrCost(campaignTotal.simulation, probabilities.MEAN) ?? 0;
  const campaignTotalCost = getDurationOrCost(campaignTotal.simulation, probabilities.MEAN, 1) ?? 0;

  const cumulativeTotalDuration = estimates.reduce(
    (sum: number, estimate: any) => sum + (getDurationOrCost(estimate.simulation, probabilities.MEAN) ?? 0),
    0,
  );
  const cumulativeTotalCost = estimates.reduce(
    (sum: number, estimate: any) => sum + (getDurationOrCost(estimate.simulation, probabilities.MEAN, 1) ?? 0),
    0,
  );

  const baseTime = Math.max(Math.abs(campaignTotalDuration), Math.abs(cumulativeTotalDuration));
  const baseCost = Math.max(Math.abs(campaignTotalCost), Math.abs(cumulativeTotalCost));

  const timeDiffPercent =
    baseTime === 0 ? 0 : (Math.abs(campaignTotalDuration - cumulativeTotalDuration) / baseTime) * 100;
  const costDiffPercent = baseCost === 0 ? 0 : (Math.abs(campaignTotalCost - cumulativeTotalCost) / baseCost) * 100;
  const diff = Math.max(timeDiffPercent, costDiffPercent);
  return diff < thresholdPercent;
};
